import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowRightLeft,
  Building2,
  ClipboardList,
  PackageCheck,
  TruckIcon,
  Warehouse,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Column,
  DataTable,
  KPIGrid,
  LoadingState,
  MetricCard,
  PageHeader,
  TableFilters,
} from '@/components/common';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  consignmentApi,
  type CallOff,
  type CallOffStatus,
  type ConsignmentNode,
  type ConsignmentPositionLine,
  type ConsignmentPositions,
  type ConsignmentSummary,
  type ReplenishmentLine,
  type SlaCompliance,
  type SlaCoverage,
} from '@/api/services';
import { AgreeLevelsDialog } from '@/components/consignment/AgreeLevelsDialog';
import { SlaPanel } from '@/components/consignment/SlaPanel';
import { RaiseCallOffDialog } from '@/components/consignment/RaiseCallOffDialog';
import { fadeUp, stagger } from '@/lib/animations';
import { cn } from '@/lib/utils';

/**
 * Our stock standing at a customer's site, and the customer drawing against it.
 *
 * The page is built around one distinction that everything else follows from:
 * what is physically at a node is not the same as what is still ours. A call-off
 * moves the second without necessarily moving the first, so both are shown side
 * by side everywhere rather than one figure labelled "stock".
 *
 * Capital is shown as unknown, never as zero, when the items at a node carry no
 * unit cost — and the count of unpriced SKUs is shown next to it, so a small
 * figure cannot be mistaken for a complete one.
 */

const STATUS_TONE: Record<CallOffStatus, string> = {
  REQUESTED: 'bg-status-warning-subtle text-status-warning-foreground',
  APPROVED: 'bg-status-info-subtle text-status-info-foreground',
  FULFILLED: 'bg-status-info-subtle text-status-info-foreground',
  COLLECTED: 'bg-status-success-subtle text-status-success-foreground',
  REJECTED: 'bg-status-destructive-subtle text-status-destructive-foreground',
  CANCELLED: 'bg-muted text-muted-foreground',
};

const STATUS_LABEL: Record<CallOffStatus, string> = {
  REQUESTED: 'Requested',
  APPROVED: 'Approved',
  FULFILLED: 'Title passed',
  COLLECTED: 'Collected',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',
};

/** Money, or an honest blank. A dash is not a zero. */
function money(value: number | null | undefined): string {
  if (value == null) return '—';
  return value.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

export default function Consignment() {
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('nodes');
  const [search, setSearch] = useState('');

  const [summary, setSummary] = useState<ConsignmentSummary | null>(null);
  const [nodes, setNodes] = useState<ConsignmentNode[]>([]);
  const [callOffs, setCallOffs] = useState<CallOff[]>([]);
  const [replenishment, setReplenishment] = useState<ReplenishmentLine[]>([]);
  const [sla, setSla] = useState<SlaCompliance[]>([]);
  const [coverage, setCoverage] = useState<SlaCoverage[]>([]);

  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [positions, setPositions] = useState<ConsignmentPositions | null>(null);
  const [positionsLoading, setPositionsLoading] = useState(false);
  const [acting, setActing] = useState<string | null>(null);
  const [editingLevels, setEditingLevels] = useState<ConsignmentPositionLine | null>(null);

  const load = useCallback(async () => {
    try {
      const [s, n, c, r, sl, cov] = await Promise.all([
        consignmentApi.summary(),
        consignmentApi.nodes(),
        consignmentApi.callOffs(),
        consignmentApi.replenishment(),
        consignmentApi.sla(),
        consignmentApi.slaCoverage(),
      ]);
      setSummary(s);
      setNodes(n);
      setCallOffs(c);
      setReplenishment(r.lines);
      setSla(sl);
      setCoverage(cov);
      setSelectedNode((prev) => prev ?? n[0]?.id ?? null);
    } catch {
      toast.error('Could not load consignment data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!selectedNode) return;
    setPositionsLoading(true);
    consignmentApi
      .positions(selectedNode)
      .then(setPositions)
      .catch(() => toast.error('Could not load the position at that node'))
      .finally(() => setPositionsLoading(false));
  }, [selectedNode]);

  /** Act, then reload — a call-off changes stock, so the whole page is stale. */
  const act = async (id: string, label: string, fn: () => Promise<unknown>) => {
    setActing(id);
    try {
      await fn();
      toast.success(label);
      await load();
      if (selectedNode) setPositions(await consignmentApi.positions(selectedNode));
    } catch (error) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'That did not work';
      toast.error(message);
    } finally {
      setActing(null);
    }
  };

  const matches = useCallback(
    (haystack: string[]) =>
      !search || haystack.some((h) => h?.toLowerCase().includes(search.toLowerCase())),
    [search]
  );

  // ── columns ──────────────────────────────────────────────────────────────

  const nodeColumns: Column<ConsignmentNode>[] = [
    {
      key: 'code',
      header: 'Node',
      sortable: true,
      linkTo: (n) => `/locations/${n.id}`,
      render: (n) => (
        <div>
          <div className="font-medium">{n.code}</div>
          <div className="text-xs text-muted-foreground">{n.name}</div>
        </div>
      ),
    },
    {
      key: 'customer',
      header: 'Customer',
      accessorFn: (n) => n.customer?.name ?? '',
      render: (n) => n.customer?.name ?? '—',
    },
    { key: 'skuCount', header: 'SKUs', sortable: true, className: 'text-right tabular-nums' },
    {
      key: 'onSiteQty',
      header: 'On site',
      sortable: true,
      className: 'text-right tabular-nums',
      render: (n) => n.onSiteQty.toLocaleString(),
    },
    {
      key: 'ourQty',
      header: 'Still ours',
      sortable: true,
      className: 'text-right tabular-nums',
      render: (n) => n.ourQty.toLocaleString(),
    },
    {
      key: 'customerOwnedQty',
      header: 'Theirs, uncollected',
      sortable: true,
      className: 'text-right tabular-nums',
      render: (n) =>
        n.customerOwnedQty > 0 ? (
          <span className="font-medium text-status-info-foreground">
            {n.customerOwnedQty.toLocaleString()}
          </span>
        ) : (
          <span className="text-muted-foreground">0</span>
        ),
    },
    {
      key: 'ourCapitalAtRisk',
      header: 'Our capital there',
      sortable: true,
      className: 'text-right tabular-nums',
      render: (n) => (
        <div>
          <div>{money(n.ourCapitalAtRisk)}</div>
          {n.unpricedSkus > 0 && (
            // Naming what the figure excludes, beside the figure. A capital
            // number computed over eight of ten SKUs reads as complete unless
            // it says otherwise.
            <div className="text-xs text-status-warning-foreground">
              {n.unpricedSkus} unpriced
            </div>
          )}
        </div>
      ),
    },
  ];

  const positionColumns: Column<ConsignmentPositionLine>[] = [
    {
      key: 'sku',
      header: 'SKU',
      sortable: true,
      linkTo: (l) => `/inventory/${l.itemId}`,
      render: (l) => (
        <div>
          <div className="font-medium">{l.sku}</div>
          <div className="text-xs text-muted-foreground">{l.name}</div>
        </div>
      ),
    },
    {
      key: 'onSiteQty',
      header: 'On site',
      sortable: true,
      className: 'text-right tabular-nums',
    },
    { key: 'ourQty', header: 'Still ours', sortable: true, className: 'text-right tabular-nums' },
    {
      key: 'customerOwnedQty',
      header: 'Theirs',
      sortable: true,
      className: 'text-right tabular-nums',
      render: (l) =>
        l.customerOwnedQty > 0 ? (
          <span className="font-medium text-status-info-foreground">{l.customerOwnedQty}</span>
        ) : (
          <span className="text-muted-foreground">0</span>
        ),
    },
    {
      key: 'reservedQty',
      header: 'Reserved',
      sortable: true,
      className: 'text-right tabular-nums',
      render: (l) => (l.reservedQty > 0 ? l.reservedQty : <span className="text-muted-foreground">0</span>),
    },
    {
      key: 'availableToCallOff',
      header: 'Free to call off',
      sortable: true,
      className: 'text-right tabular-nums',
      render: (l) => <span className="font-medium">{l.availableToCallOff}</span>,
    },
    {
      key: 'minLevel',
      header: 'Agreed min / max',
      className: 'text-right tabular-nums',
      accessorFn: (l) => l.minLevel ?? -1,
      render: (l) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setEditingLevels(l);
          }}
          className="rounded-sm text-right underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {l.minLevel == null && l.maxLevel == null ? (
            // Not agreed is not zero, and it is not a low bar cleared either.
            // It is a decision outstanding, drawn as one.
            <span className="text-status-warning-foreground">not agreed</span>
          ) : (
            <span>
              <span className="block">
                {l.minLevel ?? '—'} / {l.maxLevel ?? '—'}
              </span>
              {l.levelsAgreedBy && (
                <span className="block text-[11px] font-normal text-muted-foreground">
                  {l.levelsAgreedBy}
                </span>
              )}
            </span>
          )}
        </button>
      ),
    },
    {
      key: 'belowMin',
      header: 'Cover',
      accessorFn: (l) => (l.minLevel != null ? l.ourQty - l.minLevel : 9999),
      render: (l) => {
        // No agreed minimum means nothing can say whether the node is short.
        // Reporting "ok" here would be an answer to a question nobody can ask.
        if (l.minLevel == null)
          return <span className="text-status-warning-foreground">no standard</span>;
        return l.ourQty < l.minLevel ? (
          <Badge className="bg-status-destructive-subtle text-status-destructive-foreground">
            below min
          </Badge>
        ) : (
          <span className="text-muted-foreground">ok</span>
        );
      },
    },
  ];

  const callOffColumns: Column<CallOff>[] = [
    {
      key: 'reference',
      header: 'Reference',
      sortable: true,
      linkTo: (c) => `/consignment/call-offs/${c.id}`,
      render: (c) => <span className="font-medium">{c.reference}</span>,
    },
    {
      key: 'location',
      header: 'Node',
      accessorFn: (c) => c.location.code,
      render: (c) => (
        <div>
          <div>{c.location.code}</div>
          <div className="text-xs text-muted-foreground">{c.customerCompany.name}</div>
        </div>
      ),
    },
    {
      key: 'lines',
      header: 'Lines',
      accessorFn: (c) => c.lines.length,
      render: (c) => (
        <div className="space-y-0.5">
          {c.lines.slice(0, 2).map((l) => (
            <div key={l.id} className="text-xs">
              <span className="font-mono">{l.item.sku}</span>{' '}
              <span className="text-muted-foreground">× {l.quantityRequested}</span>
            </div>
          ))}
          {c.lines.length > 2 && (
            <div className="text-xs text-muted-foreground">+{c.lines.length - 2} more</div>
          )}
        </div>
      ),
    },
    {
      key: 'value',
      header: 'Value',
      className: 'text-right tabular-nums',
      accessorFn: (c) =>
        c.lines.reduce((s, l) => s + (l.unitPrice ?? 0) * l.quantityFulfilled, 0),
      render: (c) => {
        const settled = c.status === 'FULFILLED' || c.status === 'COLLECTED';
        if (!settled) return <span className="text-muted-foreground">—</span>;
        const priced = c.lines.filter((l) => l.unitPrice != null);
        if (!priced.length) return <span className="text-muted-foreground">unpriced</span>;
        const total = priced.reduce((s, l) => s + (l.unitPrice as number) * l.quantityFulfilled, 0);
        return (
          <div>
            <div>{money(total)}</div>
            {priced.length < c.lines.length && (
              <div className="text-xs text-status-warning-foreground">
                {c.lines.length - priced.length} unpriced
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (c) => (
        <div className="space-y-1">
          <Badge className={cn('font-normal', STATUS_TONE[c.status])}>
            {STATUS_LABEL[c.status]}
          </Badge>
          {c.status === 'FULFILLED' && (
            <div className="text-xs text-muted-foreground">standing at the node</div>
          )}
        </div>
      ),
    },
    {
      key: 'requestedAt',
      header: 'Raised',
      sortable: true,
      render: (c) => (
        <div>
          <div className="text-sm">{new Date(c.requestedAt).toLocaleDateString()}</div>
          {c.requestedBy && (
            <div className="text-xs text-muted-foreground">{c.requestedBy.fullName}</div>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (c) => {
        const busy = acting === c.id;
        // An absent action is honest; a disabled one that cannot ever apply is
        // noise. Each state offers only what it can actually do next.
        if (c.status === 'REQUESTED') {
          return (
            <div className="flex gap-1.5">
              <Button
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => act(c.id, `${c.reference} approved`, () => consignmentApi.approve(c.id))}
              >
                Approve
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => act(c.id, `${c.reference} rejected`, () => consignmentApi.reject(c.id))}
              >
                Reject
              </Button>
            </div>
          );
        }
        if (c.status === 'APPROVED') {
          return (
            <div className="flex gap-1.5">
              <Button
                size="sm"
                disabled={busy}
                onClick={() =>
                  act(c.id, `${c.reference} drawn and collected`, () =>
                    consignmentApi.fulfil(c.id, false)
                  )
                }
              >
                Draw &amp; collect
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() =>
                  act(c.id, `${c.reference}: title passed, goods retained`, () =>
                    consignmentApi.fulfil(c.id, true)
                  )
                }
              >
                Title only
              </Button>
            </div>
          );
        }
        if (c.status === 'FULFILLED') {
          return (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => act(c.id, `${c.reference} collected`, () => consignmentApi.collect(c.id))}
            >
              Record collection
            </Button>
          );
        }
        return null;
      },
    },
  ];

  // ── derived views ────────────────────────────────────────────────────────

  const replenishColumns: Column<ReplenishmentLine>[] = [
    {
      key: 'sku',
      header: 'Item',
      sortable: true,
      linkTo: (l) => `/inventory/${l.itemId}`,
      render: (l) => (
        <div>
          <div className="font-medium">{l.sku}</div>
          <div className="text-xs text-muted-foreground">{l.name}</div>
        </div>
      ),
    },
    {
      key: 'nodeCode',
      header: 'Node',
      sortable: true,
      render: (l) => (
        <div>
          <div>{l.nodeCode}</div>
          <div className="text-xs text-muted-foreground">{l.customer}</div>
        </div>
      ),
    },
    {
      key: 'ours',
      header: 'Ours / agreed',
      className: 'text-right tabular-nums',
      render: (l) => (
        <div>
          <div>
            <span className="font-medium text-status-destructive-foreground">{l.ours}</span>
            <span className="text-muted-foreground"> / {l.agreedMin}</span>
          </div>
          <div className="text-[11px] text-muted-foreground">short by {l.shortBy}</div>
        </div>
      ),
    },
    {
      key: 'inTransit',
      header: 'On its way',
      sortable: true,
      className: 'text-right tabular-nums',
      render: (l) =>
        l.inTransit > 0 ? (
          <span className="font-medium text-status-info-foreground">{l.inTransit}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'suggestedQty',
      header: 'Move',
      sortable: true,
      className: 'text-right tabular-nums',
      render: (l) =>
        // Nothing to move is three different situations, and they send somebody
        // to three different places. Saying "0" would send them nowhere.
        l.coveredByInbound ? (
          <span className="text-xs text-status-info-foreground">already covered</span>
        ) : l.suggestedQty > 0 ? (
          <div>
            <span className="font-medium">{l.suggestedQty}</span>
            <div className="text-[11px] text-muted-foreground">to reach {l.topUpTo}</div>
          </div>
        ) : (
          <span className="text-xs text-status-warning-foreground">nothing to send</span>
        ),
    },
    {
      key: 'coverableFrom',
      header: 'From',
      accessorFn: (l) => l.coverableFrom?.code ?? '',
      render: (l) =>
        l.coverableFrom ? (
          <div>
            <div>{l.coverableFrom.code}</div>
            <div className="text-[11px] text-muted-foreground">
              can spare {l.coverableFrom.canSpare}
              {!l.coverableFrom.sourceFloorAgreed && (
                // The whole balance is offered because nobody has said how much
                // that location must keep. Worth saying, rather than letting the
                // number imply somebody checked.
                <span className="text-status-warning-foreground"> · no floor agreed</span>
              )}
            </div>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">{l.blocked ?? '—'}</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      render: (l) =>
        l.suggestedQty > 0 && l.coverableFrom ? (
          <Button
            size="sm"
            disabled={acting === l.itemId + l.locationId}
            onClick={() =>
              act(l.itemId + l.locationId, `${l.suggestedQty} × ${l.sku} despatched`, () =>
                consignmentApi.executeReplenishment({
                  locationId: l.locationId,
                  sourceLocationId: (l.coverableFrom as NonNullable<typeof l.coverableFrom>)
                    .locationId,
                  lines: [{ itemId: l.itemId, quantity: l.suggestedQty }],
                })
              )
            }
          >
            Send {l.suggestedQty}
          </Button>
        ) : null,
    },
  ];

  const openCallOffs = useMemo(
    () => callOffs.filter((c) => ['REQUESTED', 'APPROVED', 'FULFILLED'].includes(c.status)),
    [callOffs]
  );

  const coverageColumns: Column<SlaCoverage>[] = [
    {
      key: 'sku',
      header: 'Item',
      sortable: true,
      render: (c) => (
        <div>
          <div className="font-medium">{c.sku}</div>
          <div className="text-xs text-muted-foreground">{c.name}</div>
        </div>
      ),
    },
    { key: 'node', header: 'Node', sortable: true },
    {
      key: 'ours',
      header: 'Still ours',
      sortable: true,
      className: 'text-right tabular-nums',
    },
    {
      key: 'drawsObserved',
      header: 'Draws',
      sortable: true,
      className: 'text-right tabular-nums',
    },
    {
      key: 'coverageDays',
      header: 'Cover',
      sortable: true,
      className: 'text-right tabular-nums',
      accessorFn: (c) => c.coverageDays ?? -1,
      render: (c) =>
        c.coverageDays == null ? (
          <span className="text-xs text-muted-foreground">{c.whyNot}</span>
        ) : (
          <span className="font-medium">{c.coverageDays} days</span>
        ),
    },
  ];

  if (loading) return <LoadingState />;

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          title="Consignment"
          description="Stock held at customer sites under contract, and the call-offs that transfer title to the customer."
          actions={
            <RaiseCallOffDialog
              nodes={nodes}
              defaultNodeId={selectedNode}
              onRaised={async () => {
                setTab('call-offs');
                await load();
              }}
            />
          }
        />
      </motion.div>

      <motion.div variants={fadeUp}>
        <KPIGrid columns={4}>
          <MetricCard
            label="Consignment nodes"
            value={summary?.nodes ?? 0}
            subtitle="customer sites holding consigned stock"
            icon={<Warehouse className="h-4 w-4" />}
          />
          <MetricCard
            label="Our capital there"
            value={summary?.ourCapitalAtRisk == null ? 'Unknown' : money(summary.ourCapitalAtRisk)}
            subtitle={
              summary?.unpricedSkus
                ? `${summary.unpricedSkus} of ${summary.skusOnConsignment} SKUs carry no cost`
                : `across ${summary?.skusOnConsignment ?? 0} SKUs`
            }
            sentiment={summary?.unpricedSkus ? 'warning' : 'neutral'}
            icon={<Building2 className="h-4 w-4" />}
          />
          <MetricCard
            label="Call-offs open"
            value={summary?.openCallOffs ?? 0}
            subtitle="raised or approved, not yet drawn"
            sentiment={summary?.openCallOffs ? 'warning' : 'neutral'}
            icon={<ClipboardList className="h-4 w-4" />}
          />
          <MetricCard
            label="Awaiting collection"
            value={summary?.awaitingCollection ?? 0}
            subtitle={`${summary?.customerOwnedQty ?? 0} units are theirs and still on site`}
            icon={<PackageCheck className="h-4 w-4" />}
          />
        </KPIGrid>
      </motion.div>

      <motion.div variants={fadeUp}>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="nodes">Nodes ({nodes.length})</TabsTrigger>
            <TabsTrigger value="positions">Position at a node</TabsTrigger>
            <TabsTrigger value="replenishment">
              Replenishment{replenishment.length ? ` (${replenishment.length})` : ''}
            </TabsTrigger>
            <TabsTrigger value="call-offs">Call-offs ({openCallOffs.length} open)</TabsTrigger>
            <TabsTrigger value="performance">Performance</TabsTrigger>
          </TabsList>

          <TabsContent value="nodes" className="mt-4">
            <DataTable<ConsignmentNode>
              data={nodes.filter((n) => matches([n.code, n.name, n.customer?.name ?? '']))}
              columns={nodeColumns}
              toolbar={
                <TableFilters
                  search={search}
                  onSearchChange={setSearch}
                  searchPlaceholder="Search by node or customer…"
                />
              }
              pagination
              exportable
              columnToggle
              emptyTitle="No consignment nodes"
              emptyDescription="A location becomes a consignment node once a customer is set on it. Open a location and record which customer's site it is."
            />
          </TabsContent>

          <TabsContent value="positions" className="mt-4 space-y-4">
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">Node</span>
              <Select value={selectedNode ?? undefined} onValueChange={setSelectedNode}>
                <SelectTrigger className="w-[320px]">
                  <SelectValue placeholder="Choose a node" />
                </SelectTrigger>
                <SelectContent>
                  {nodes.map((n) => (
                    <SelectItem key={n.id} value={n.id}>
                      {n.code} — {n.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {positions?.location.customer && (
                <Badge variant="outline" className="font-normal">
                  {positions.location.customer.name}
                </Badge>
              )}
            </div>

            <DataTable<ConsignmentPositionLine>
              data={(positions?.lines ?? []).filter((l) => matches([l.sku, l.name]))}
              columns={positionColumns}
              loading={positionsLoading}
              toolbar={
                <TableFilters
                  search={search}
                  onSearchChange={setSearch}
                  searchPlaceholder="Search by SKU or name…"
                />
              }
              pagination
              exportable
              columnToggle
              emptyTitle="Nothing held at this node"
              emptyDescription="No stock is recorded against this location yet. Receive material to it, or move it there from a hub."
            />

            <AgreeLevelsDialog
              line={editingLevels}
              locationId={selectedNode ?? ''}
              onClose={() => setEditingLevels(null)}
              onSaved={async () => {
                if (selectedNode) setPositions(await consignmentApi.positions(selectedNode));
              }}
            />

            <p className="text-xs text-muted-foreground">
              <span className="font-medium">On site</span> is everything physically there,
              whoever owns it. <span className="font-medium">Still ours</span> is the part we can
              still act on, and <span className="font-medium">free to call off</span> takes off
              what is reserved as well. A call-off moves stock between the second and third columns
              without necessarily moving the first. <span className="font-medium">Agreed min
              and max</span> are contractual terms somebody recorded, not figures the system
              worked out — select one to change it.
            </p>
          </TabsContent>

          <TabsContent value="replenishment" className="mt-4">
            <DataTable<ReplenishmentLine>
              data={replenishment.filter((l) => matches([l.sku, l.name, l.nodeCode]))}
              columns={replenishColumns}
              toolbar={
                <TableFilters
                  search={search}
                  onSearchChange={setSearch}
                  searchPlaceholder="Search by SKU or node…"
                />
              }
              pagination
              exportable
              columnToggle
              emptyTitle="Nothing needs replenishing"
              emptyDescription="Every consignment node with an agreed minimum is at or above it. Nodes with no agreed level are not counted here, because nothing can say whether those are short."
            />
            <div className="mt-3 flex items-start gap-2 text-xs text-muted-foreground">
              <TruckIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <p>
                Worked out from today&rsquo;s stock against the agreed levels, and stored
                nowhere. Quantities top up to the agreed maximum, less anything already on its way.
                a transfer leaves the position on despatch and arrives on receipt, so a node stays
                short in the data while the material is in transit. There is no arrival date here,
                because transit time between two of the operator's own locations is not recorded.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="call-offs" className="mt-4">
            <DataTable<CallOff>
              data={callOffs.filter((c) =>
                matches([c.reference, c.location.code, c.customerCompany.name])
              )}
              columns={callOffColumns}
              toolbar={
                <TableFilters
                  search={search}
                  onSearchChange={setSearch}
                  searchPlaceholder="Search by reference, node or customer…"
                />
              }
              pagination
              exportable
              columnToggle
              emptyTitle="No call-offs raised"
              emptyDescription="A call-off is how a customer draws against the stock held at their site. It is the point at which title passes, so it is also what gets billed."
            />
            <div className="mt-3 flex items-start gap-2 text-xs text-muted-foreground">
              <ArrowRightLeft className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <p>
                <span className="font-medium">Draw &amp; collect</span> passes title and takes the
                material off site in one act. <span className="font-medium">Title only</span> passes
                title while the goods stay standing at the node — they leave our position but not
                the site, and a collection is recorded when they finally move.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="performance" className="mt-4 space-y-5">
            <SlaPanel rows={sla} onChanged={() => void load()} />

            <div>
              <h3 className="mb-1 text-sm font-semibold">Cover at each node</h3>
              <p className="mb-3 text-xs text-muted-foreground">
                How long the stock would last at the rate the customer actually draws it, from
                call-off history. Blank where there have been too few draws to call it a rate:
                two in ninety days is not a run-rate, and dividing by it produces a confident
                number out of noise.
              </p>
              <DataTable<SlaCoverage>
                data={coverage.filter((c) => matches([c.sku, c.name, c.node]))}
                columns={coverageColumns}
                toolbar={
                  <TableFilters
                    search={search}
                    onSearchChange={setSearch}
                    searchPlaceholder="Search by item or node…"
                  />
                }
                pagination
                exportable
                columnToggle
                emptyTitle="No cover to report"
                emptyDescription="Cover is worked out from what customers have actually drawn. Once call-offs have been fulfilled at a node, it appears here."
              />
            </div>
          </TabsContent>
        </Tabs>
      </motion.div>
    </motion.main>
  );
}
